import { useMemo } from 'react'
import { CarHireSearchFormParams } from '@reward-platform/ancillaries-schemas/car-hire'
import { differenceInYears, format, add, sub } from 'date-fns'
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { getCarLocations } from '~/services/locationService'
import { usePartner } from '~/context/partner'
import { CarHireSearchForm } from '../SearchCarHireForm/SearchCarHireForm.types'

const getTimes = (date: string) => {
  return date ? format(new Date(date), 'HH:mm') : '10:00'
}

export const useQueryStateToFormState = (
  queryState: CarHireSearchFormParams & { tab?: string }
): {
  isLoading: boolean
  formValues: CarHireSearchForm
} => {
  const { pickUpLocation, dates, dropOffLocation, travelers, tab, membershipCode, vendorBrandId } =
    queryState
  const { code: partner } = usePartner()
  const shouldSearchPickUp = pickUpLocation !== undefined && pickUpLocation.id !== ''
  const shouldSearchDropOff = dropOffLocation !== undefined && dropOffLocation.id !== ''

  const { data: pickUpLocations, isLoading: isLoadingPickUp } = useQuery({
    queryKey: ['pickUpLocation', partner, pickUpLocation?.id],
    queryFn: () => getCarLocations(partner, 'CAR_HIRE', pickUpLocation?.id, vendorBrandId),
    enabled: shouldSearchPickUp,
    placeholderData: keepPreviousData,
    meta: {
      notifyUser: true,
      errorMessage: 'Car Hire Error - could not get car hire locations',
    },
  })

  const { data: dropOffLocations, isLoading: isLoadingDropOff } = useQuery({
    queryKey: ['dropOffLocations', partner, dropOffLocation?.id],
    queryFn: () => getCarLocations(partner, 'CAR_HIRE', dropOffLocation?.id ?? '', vendorBrandId),
    enabled: shouldSearchDropOff,
    placeholderData: keepPreviousData,
    meta: {
      notifyUser: true,
      errorMessage: 'Car Hire Error - could not get car hire locations',
    },
  })

  const defaultLocation = useMemo(
    () => ({
      id: '',
      type: '',
      name: '',
      fullName: '',
      countryCode: 'GB',
      afterHoursIndicator: true,
      telephoneNumber: '',
      location: [
        {
          latitude: '',
          longitude: '',
        },
      ],
      openingTimes: [],
      address: [''],
      vendorBrandId: '1',
    }),
    []
  )

  const travellerFormValues = useMemo(
    () => ({
      isUnder30: travelers?.[0]?.dateOfBirth
        ? differenceInYears(new Date(), new Date(travelers[0]?.dateOfBirth)) < 30
        : false,
      dob: travelers?.[0]?.dateOfBirth
        ? new Date(travelers[0].dateOfBirth)
        : add(sub(new Date(), { years: 30 }), { days: 1 }),
      membershipCode: membershipCode ?? undefined,
      isAvisPreferred: membershipCode !== undefined,
    }),
    [membershipCode, travelers]
  )

  const pickUpFormValues = useMemo(
    () => ({
      pickUpSearchString: pickUpLocations?.length ? pickUpLocations[0].fullName : '',
      pickUpDate: dates?.startDateTime
        ? new Date(dates.startDateTime)
        : add(new Date(), { days: 1 }),
      pickUpTime: getTimes(dates?.startDateTime),
      pickUpLocation: pickUpLocations?.length
        ? { ...pickUpLocations[0], vendorBrandId }
        : defaultLocation,
    }),
    [dates, defaultLocation, pickUpLocations, vendorBrandId]
  )

  const dropOffFormValues = useMemo(
    () => ({
      dropOffSearchString: dropOffLocations?.length ? dropOffLocations[0].fullName : '',
      dropOffDate: dates?.endDateTime
        ? new Date(dates.endDateTime)
        : add(new Date(), { days: tab === 'carhire' ? 3 : 2 }),
      dropOffTime: getTimes(dates?.endDateTime),
      dropOffLocation: dropOffLocations?.length ? dropOffLocations[0] : defaultLocation,
    }),
    [dates, defaultLocation, dropOffLocations, tab]
  )

  return {
    isLoading: isLoadingDropOff || isLoadingPickUp,
    formValues: {
      ...pickUpFormValues,
      ...dropOffFormValues,
      ...travellerFormValues,
      differentDropOff: !!(dropOffLocation && pickUpLocation.id !== dropOffLocation.id),
    },
  }
}
